<script lang="ts">
import { computed, defineComponent, h, nextTick, ref, unref, watch } from 'vue';

import { classNames, keyNames, useCache, wrapInArray } from '@components/utils';

import {
  CdsTransition,
  getDayHash,
  makeDatetimeProps,
  makeDisabledProps,
  makeLocaleProps,
  makeReadonlyProps,
  makeThemeProps,
  provideTheme,
  useDatetime,
  useProxyModel,
  useRender
} from '../../composable';
import { CdsButton, CdsFunctionalButton } from '../CdsButton';

import { makeCalendarProps, lineStr, useCalendar, YEARS_INTERVAL } from './composable';

import type { TCalendarView } from './composable';
import type { TDatetimeValueRange } from '../../composable';
import type { IRawDate } from '@components/utils';

export const componentName = 'CdsDatePicker';
export default defineComponent({
  name: componentName,
  props: {
    ...makeCalendarProps(),
    ...makeDatetimeProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLocaleProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { value: string | TDatetimeValueRange | Array<string | TDatetimeValueRange>, reason: string, details: IRawDate }
     */
    'update:modelValue': (...args: any) => true,
    /**
     * Событие при переходе к другому месяцу или году (при изменении модели из вне также будет срабатывать)
     */
    navigation: ({ year, month }: { year: number | undefined; month: number | undefined }) => true,
    /**
     * Событие начала выбора диапазона дат
     */
    rangeStart: (value: Partial<IRawDate>) => true,
    /**
     * Событие окончания выбора диапазона дат
     */
    rangeEnd: ({ from, to }: { from: Partial<IRawDate>; to: Partial<IRawDate> }) => true
  },
  setup(props, { slots, emit, expose }) {
    const { themeClasses } = provideTheme(props);
    const model = useProxyModel(props, 'modelValue', [], (value) => wrapInArray(value));
    const { tabindex, getCurrentDate, getShortDate, getLocale, getMask, decodeString } = useDatetime(props);
    const internalLocale = ref(getLocale());
    const internalMask = ref(getMask());
    const {
      today,
      editRange,
      getNativeDateFn,
      defaultView,
      monthDirection,
      yearDirection,
      viewModel,
      daysModel,
      minSelectedModel,
      maxSelectedModel,
      daysInModel,
      startYear,
      daysOfWeek,
      viewMonthHash,
      minNav,
      maxNav,
      navBoundaries,
      days,
      getViewModel,
      updateViewModel,
      setToday,
      setCalendarTo,
      goToMonth,
      goToYear,
      setMonth,
      setYear,
      setView,
      setEditingRange,
      toggleDate,
      addToModel,
      addToModelRange,
      removeFromModel,
      removeFromModelRange,
      offsetCalendar,
      encodeEntry
    } = useCalendar(
      props,
      model,
      internalLocale,
      internalMask,
      getCurrentDate,
      decodeString,
      emitValue,
      emitImmediately
    );

    const { getCache } = useCache();

    const pickerRef = ref<HTMLElement>();
    const wrapperRef = ref<HTMLElement>();

    let lastEmitValue: any;

    const headerTitle = computed(() => {
      if (props.title) return props.title;

      if (unref(editRange)) {
        const model = unref(editRange)!.init;

        if (model) {
          const date = unref(getNativeDateFn)(model);
          return (
            unref(internalLocale).daysShort[date.getDay()] +
            ', ' +
            model.day +
            ' ' +
            unref(internalLocale).monthsLong[(model.month as number) - 1] +
            lineStr +
            '?'
          );
        }
      }

      if (!unref(daysInModel)) return lineStr;
      if (unref(daysInModel) > 1) return `${unref(daysInModel)} ${unref(internalLocale).pluralDay(unref(daysInModel))}`;

      const model = unref(daysModel)[0];
      const date = unref(getNativeDateFn)(model);

      if (isNaN(date.valueOf())) return lineStr;

      return (
        unref(internalLocale).daysShort[date.getDay()] +
        ', ' +
        model.day +
        ' ' +
        unref(internalLocale).monthsLong[(model.month as number) - 1]
      );
    });
    const headerDescription = computed(() => {
      if (props.description) return props.description;
      if (unref(daysInModel) === 0) return lineStr;

      if (unref(daysInModel) > 1) {
        const from = unref(minSelectedModel);
        const to = unref(maxSelectedModel);
        const month = unref(internalLocale).monthsShort;

        return (
          month[(from.month as number) - 1] +
          (from.year !== to.year
            ? ' ' + from.year + lineStr + month[(to.month as number) - 1] + ' '
            : from.month !== to.month
            ? lineStr + month[(to.month as number) - 1]
            : '') +
          ' ' +
          to.year
        );
      }

      return unref(daysModel)[0].year;
    });

    const attributes = computed(() => {
      if (props.disabled) return { 'aria-disabled': 'true' };
      if (props.readonly) return { 'aria-readonly': 'true' };
      return {};
    });
    const classes = computed(() => ({
      'cds-date-picker': true,
      'cds-date-picker--is-header': props.header || props.title,
      'cds-date-picker--disabled': props.disabled,
      'cds-date-picker--readonly': props.readonly
    }));

    watch(
      () => model,
      (value) => {
        if (lastEmitValue === value) {
          lastEmitValue = 0;
        } else {
          const newModel = getViewModel();
          updateViewModel(newModel.year as number, newModel.month as number, newModel);
        }
      }
    );

    watch(defaultView, () => {
      if (unref(pickerRef) && unref(wrapperRef) && unref(wrapperRef)!.contains(document.activeElement)) {
        unref(pickerRef)!.focus();
      }
    });

    watch(
      () => unref(viewModel).year + '|' + unref(viewModel).month,
      () => {
        emit('navigation', { year: unref(viewModel).year, month: unref(viewModel).month });
      }
    );

    function emitValue(value: any, action: string, date: IRawDate | TDatetimeValueRange) {
      const val = value && value.length === 1 && !props.multiple ? value[0] : value;
      lastEmitValue = val;

      const { reason, details } = getEmitParams(action, date);
      emit('update:modelValue', val, reason, details);
    }

    function emitImmediately(reason: string) {
      const date =
        unref(daysModel)[0] && unref(daysModel)[0].dateHash ? { ...unref(daysModel)[0] } : { ...unref(viewModel) };

      nextTick(() => {
        date.year = unref(viewModel).year;
        date.month = unref(viewModel).month;

        const maxDay = new Date(date.year as number, date.month as number, 0).getDate();

        date.day = Math.min(Math.max(1, date.day as number), maxDay);

        const value = encodeEntry(date);
        lastEmitValue = value;

        const { details } = getEmitParams('', date);

        emit('update:modelValue', value, reason, details);
      });
    }

    function getEmitParams(action: string, date: IRawDate | TDatetimeValueRange) {
      return (date as TDatetimeValueRange).from
        ? {
            reason: `${action}-range`,
            details: {
              ...getShortDate((date as TDatetimeValueRange).target as IRawDate),
              from: getShortDate((date as TDatetimeValueRange).from as IRawDate),
              to: getShortDate((date as TDatetimeValueRange).to as IRawDate)
            }
          }
        : {
            reason: `${action}-day`,
            details: getShortDate(date as IRawDate)
          };
    }

    function onDayClick(dayIdx: number) {
      const day = { ...unref(viewModel), day: dayIdx };

      if (!props.range) {
        toggleDate(day, unref(viewMonthHash));
        return;
      }

      if (!unref(editRange)) {
        const dayProps = unref(days).find((day) => !day.fill && day.i === dayIdx);

        if (!props.noUnset && dayProps?.range) {
          removeFromModelRange({ target: day, from: dayProps.range.from, to: dayProps.range.to });
          return;
        }

        if (dayProps?.selected) {
          removeFromModel(day);
          return;
        }

        const initHash = getDayHash(day) as unknown as number;

        editRange.value = {
          init: day,
          initHash,
          final: day,
          finalHash: initHash
        };

        emit('rangeStart', getShortDate(day));
      } else {
        const initHash = unref(editRange)!.initHash;
        const finalHash = getDayHash(day) as unknown as number;
        const payload =
          initHash <= finalHash
            ? { from: unref(editRange)!.init, to: day }
            : ({ from: day, to: unref(editRange)!.init } as any);

        editRange.value = null;

        if (initHash === finalHash) {
          addToModel(day);
        } else {
          addToModelRange({ target: day, ...payload });
        }

        emit('rangeEnd', {
          from: getShortDate(payload.from),
          to: getShortDate(payload.to)
        });
      }
    }

    function onDayMouseover(dayIdx: number) {
      if (unref(editRange)) {
        const final = { ...unref(viewModel), day: dayIdx };

        editRange.value = {
          ...unref(editRange)!,
          final,
          finalHash: getDayHash(final) as unknown as number
        };
      }
    }

    function renderHeader() {
      if (props.header || props.title) {
        return h('div', { class: 'cds-date-picker__header' }, [
          h('div', { class: 'cds-date-picker__header-title' }, [
            h(
              CdsTransition,
              { transition: 'fade-transition-minimal' },
              {
                default: () =>
                  slots.title
                    ? /**
                       * @slot Заголовок календаря
                       * @type HTMLElement
                       */
                      slots.title({ date: unref(headerTitle) })
                    : h(
                        'div',
                        {
                          key: 'header-title-' + unref(headerTitle),
                          class: [
                            'cds-date-picker__header-label',
                            {
                              'cds-date-picker__header-label--active': unref(defaultView) === 'Calendar'
                            }
                          ],
                          ...getCache('viewCalendar', {
                            onClick() {
                              defaultView.value = 'Calendar';
                            },
                            onKeyup(event: KeyboardEvent) {
                              event.key === keyNames.enter && (defaultView.value = 'Calendar');
                            }
                          })
                        },
                        [unref(headerTitle)]
                      )
              }
            ),

            props.todayBtn
              ? h(CdsFunctionalButton, {
                  class: 'cds-date-picker__header-today',
                  icon: 'calendar',
                  title: unref(internalLocale).today,
                  tabindex: unref(tabindex),
                  onClick: setToday
                })
              : null
          ]),

          h(
            'div',
            {
              class: 'cds-date-picker__header-description'
            },
            [
              h(
                CdsTransition,
                { transition: 'fade-transition-minimal' },
                {
                  default: () =>
                    slots.description
                      ? /**
                         * @slot Описание заголовка
                         * @type HTMLElement
                         */
                        slots.description({ year: unref(headerDescription) })
                      : h(
                          'div',
                          {
                            key: 'header-year-' + unref(headerDescription),
                            class: [
                              'cds-date-picker__header-year',
                              { 'cds-date-picker__header-year--active': unref(defaultView) === 'Years' }
                            ],
                            ...getCache('viewYear', {
                              onClick() {
                                defaultView.value = 'Years';
                              },
                              onKeyup(event: KeyboardEvent) {
                                event.key === keyNames.enter && (defaultView.value = 'Years');
                              }
                            })
                          },
                          [unref(headerDescription)]
                        )
                }
              )
            ]
          )
        ]);
      }
    }

    function renderNavigation({
      label,
      type,
      key,
      direction,
      goTo,
      boundaries,
      classname
    }: {
      label: string;
      type: TCalendarView;
      key: number;
      direction: 'left' | 'right';
      goTo: (offset: number) => void;
      boundaries: { prev: boolean; next: boolean };
      classname: string;
    }) {
      return [
        h('div', { class: 'cds-date-picker__arrow' }, [
          h(CdsFunctionalButton, {
            icon: 'chevron-left',
            disabled: !boundaries.prev,
            tabindex: unref(tabindex),
            ...getCache('go-#' + type, {
              onClick() {
                goTo(-1);
              }
            })
          })
        ]),
        h('div', { class: `${classname}` }, [
          h(
            CdsTransition,
            {
              transition: `jump-transition-${direction}`
            },
            () =>
              h('div', { key }, [
                h(CdsFunctionalButton, {
                  text: String(label),
                  tabindex: unref(tabindex),
                  ...getCache('view#' + type, {
                    onClick: () => {
                      defaultView.value = type;
                    }
                  })
                })
              ])
          )
        ]),
        h('div', { class: 'cds-date-picker__arrow' }, [
          h(CdsFunctionalButton, {
            icon: 'chevron-right',
            disabled: !boundaries.next,
            tabindex: unref(tabindex),
            ...getCache('go+#' + type, {
              onClick() {
                goTo(1);
              }
            })
          })
        ])
      ];
    }

    const renderViews = {
      Calendar: () => [
        h('div', { key: 'calendar-view', class: 'cds-date-picker__view cds-date-picker__calendar' }, [
          h('div', { class: 'cds-date-picker__navigation' }, [
            ...renderNavigation({
              label: unref(internalLocale).months[(unref(viewModel).month as number) - 1],
              type: 'Months',
              key: unref(viewModel).month as number,
              direction: unref(monthDirection),
              goTo: goToMonth,
              boundaries: unref(navBoundaries).month,
              classname: 'cds-date-picker__navigation--months'
            }),
            ...renderNavigation({
              label: String(unref(viewModel).year as number),
              type: 'Years',
              key: unref(viewModel).year as number,
              direction: unref(yearDirection),
              goTo: goToYear,
              boundaries: unref(navBoundaries).year,
              classname: 'cds-date-picker__navigation--years'
            })
          ]),
          h(
            'div',
            { class: 'cds-date-picker__calendar-weekdays' },
            unref(daysOfWeek).map((day) => h('div', { class: 'cds-date-picker__weekdays-item' }, [h('div', day)]))
          ),
          h(
            'div',
            { class: 'cds-date-picker__calendar-days-container' },
            h(CdsTransition, { transition: `slide-transition-${unref(monthDirection)}` }, () =>
              h(
                'div',
                { key: unref(viewMonthHash), class: 'cds-date-picker__calendar-days' },
                unref(days).map((day) =>
                  h(
                    'div',
                    {
                      class: [
                        day.classes,
                        {
                          'cds-date-picker__calendar-item--today': day.today,
                          'cds-date-picker__calendar-item--active': !day.flat,
                          [`cds-date-picker__calendar-item--event--${day.event}`]: day.event
                        }
                      ]
                    },
                    [
                      day.in
                        ? h(CdsButton, {
                            appearance: day.flat ? 'transparent' : 'primary',
                            class: ['cds-date-picker__calendar-day'],
                            text: String(day.i),
                            tabindex: unref(tabindex),
                            ...getCache('day#' + day.i, {
                              onClick: () => {
                                onDayClick(day.i);
                              },
                              onMouseover: () => {
                                onDayMouseover(day.i);
                              }
                            })
                          })
                        : h('div', !day.fill ? day.i : '')
                    ]
                  )
                )
              )
            )
          )
        ])
      ],
      Months: () => {
        const currentYear = unref(viewModel).year === unref(today).year;
        const isDisabled = (month: number) =>
          (minNav && unref(viewModel).year === unref(minNav)?.year && (unref(minNav)?.month as number) > month) ||
          (maxNav && unref(viewModel).year === unref(maxNav)?.year && (unref(maxNav)?.month as number) < month);

        return h(
          'div',
          { key: 'months-view', class: 'cds-date-picker__view cds-date-picker__months' },
          unref(internalLocale).months.map((month, i) => {
            const active = (unref(viewModel).month as number) === i + 1;

            return h('div', { class: 'cds-date-picker__months-item' }, [
              h(CdsButton, {
                appearance: active ? 'primary' : 'transparent',
                class: [
                  'cds-date-picker__month',
                  { 'cds-date-picker__month--today': currentYear && unref(today).month === i + 1 },
                  { 'cds-date-picker__month--active': active }
                ],
                size: 'sm',
                text: String(month),
                disabled: isDisabled(i + 1),
                tabindex: unref(tabindex),
                ...getCache('month#' + i, {
                  onClick: () => {
                    setMonth(i + 1);
                  }
                })
              })
            ]);
          })
        );
      },
      Years: () => {
        const start = unref(startYear) as number;
        const stop = start + YEARS_INTERVAL;
        const years = [];

        const isDisabled = (year: number) =>
          (unref(minNav) && unref(minNav)!.year > year) || (unref(maxNav) && unref(maxNav)!.year < year);

        for (let i = start; i <= stop; i++) {
          const active = unref(viewModel).year === i;

          years.push(
            h('div', { class: 'cds-date-picker__years-item' }, [
              h(CdsButton, {
                key: `year-${i}`,
                appearance: active ? 'primary' : 'transparent',
                class: [
                  'cds-date-picker__year',
                  { 'cds-date-picker__year--active': active },
                  { 'cds-date-picker__year--today': unref(today).year === i }
                ],
                text: String(i),
                size: 'sm',
                disabled: isDisabled(i),
                tabindex: unref(tabindex),
                ...getCache('year#' + i, {
                  onClick: () => {
                    setYear(i);
                  }
                })
              })
            ])
          );
        }

        return h('div', { key: 'years-view', class: 'cds-date-picker__view cds-date-picker__years' }, [
          h('div', { class: 'cds-date-picker__years-container' }, [
            h('div', { class: 'cds-date-picker__years-prev' }, [
              h(CdsFunctionalButton, {
                icon: 'chevron-left',
                disabled: isDisabled(start),
                tabindex: unref(tabindex),
                ...getCache('year-prev', {
                  onClick: () => {
                    (startYear.value as number) -= YEARS_INTERVAL;
                  }
                })
              })
            ]),

            h('div', { class: 'cds-date-picker__years-body' }, years),

            h('div', { class: 'cds-date-picker__years-next' }, [
              h(CdsFunctionalButton, {
                icon: 'chevron-right',
                disabled: isDisabled(stop),
                tabindex: unref(tabindex),
                ...getCache('year-next', {
                  onClick: () => {
                    (startYear.value as number) += YEARS_INTERVAL;
                  }
                })
              })
            ])
          ])
        ]);
      }
    };

    useRender(() => {
      return h(
        'div',
        {
          ref: wrapperRef,
          class: classNames(unref(classes), unref(themeClasses)),
          ...unref(attributes)
        },
        [
          renderHeader(),
          h(
            'div',
            {
              ref: pickerRef,
              class: 'cds-date-picker__container',
              tabindex: -1
            },
            h(
              'div',
              {
                class: 'cds-date-picker__body'
              },
              [
                h(
                  CdsTransition,
                  {
                    appear: true,
                    transition: 'fade-transition-minimal'
                  },
                  renderViews[unref(defaultView)]
                ),
                /**
                 * @slot Для дополнительных кнопок
                 * @type HTMLElement
                 */
                slots.default ? h('div', { class: 'cds-date-picker__actions' }, [slots.default?.()]) : null
              ]
            )
          )
        ]
      );
    });

    expose({
      setToday,
      setCalendarTo,
      setView,
      offsetCalendar,
      setEditingRange
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsDatePicker';
</style>
